/**
 * Console twin of the product's `#/shared/lib/telephony` surface.
 *
 * The console has no softphone stack, so the store is a real zustand store
 * frozen in the product's own "phone down" state — every vendored call
 * surface then renders its disconnected/disabled state, `tel:` fallbacks
 * stay clickable (`phoneUp` gates those), and the call-control functions
 * are inert. The type module is the product's own file, verbatim.
 *
 * Only what the vendored trees import is mirrored; the Telnyx engine,
 * cross-tab coordination, and preflight live only in the product.
 */
import { create } from 'zustand'
import type { QueryClient } from '@tanstack/react-query'

import { PHONE_STATE } from './model/types'
import type {
  LiveCallQuality,
  PhoneState,
  TelephonyCall,
} from './model/types'

export {
  PHONE_STATE,
  CALL_SESSION_STATE,
  CALL_DIRECTION,
  CALL_SESSION_EVENT,
} from './model/types'
export type {
  PhoneState,
  CallSessionState,
  CallDirection,
  CallQuality,
  DisconnectReason,
  LiveCallQuality,
  TelephonyCall,
} from './model/types'

/* ----------------------------------------------------------------- dialing */

/** Why the call button is disabled — the tooltip's message key. */
export type CallDisabledReason =
  | 'connecting'
  | 'offline'
  | 'busy'
  | 'loadingLines'
  | 'lineCannotCall'

/**
 * Which surface owns the placed call's UI: a 'panel' call renders inside the
 * phone panel, a 'global' one in the header pill.
 */
export type DialSurface = 'panel' | 'global'

export interface StartCall {
  call: (number: string) => boolean
  ready: boolean
  phoneUp: boolean
  disabledReason: CallDisabledReason | null
}

const PHONE_DOWN: StartCall = {
  call: () => false,
  ready: false,
  phoneUp: false,
  disabledReason: 'offline',
}

export function useStartCall(_surface: DialSurface = 'global'): StartCall {
  return PHONE_DOWN
}

/* ------------------------------------------------------------------- store */

interface OutboundLine {
  callerId: string
  canCall: boolean
  loading: boolean
}

interface CallTimings {
  ringingAt?: number
  answeredAt?: number
  connectedAt?: number
  endedAt?: number
}

interface EndedCall {
  callId: string
  remoteNumber: string
  endedAt: number
  session: TelephonyCall
}

interface TelephonyTwinState {
  status: PhoneState
  connected: boolean
  disconnectReason: null
  micBlocked: boolean
  primaryNumber: string
  outboundLine: OutboundLine
  callerIds: string[]
  sessions: TelephonyCall[]
  callTimings: Record<string, CallTimings | undefined>
  lastEnded: EndedCall | undefined
  attentionAt: number
  tabId: string
  presenters: Record<string, string>
  panelCalls: string[]
  silencedCalls: string[]
  callQuality: Record<string, LiveCallQuality | undefined>
  clearEnded: () => void
  requestAttention: () => void
  setOutboundLine: (outboundLine: OutboundLine) => void
}

export const useTelephonyStore = create<TelephonyTwinState>(() => ({
  status: PHONE_STATE.offline,
  connected: false,
  disconnectReason: null,
  micBlocked: false,
  primaryNumber: '',
  outboundLine: { callerId: '', canCall: false, loading: false },
  callerIds: [],
  sessions: [],
  callTimings: {},
  lastEnded: undefined,
  attentionAt: 0,
  tabId: 'console',
  presenters: {},
  panelCalls: [],
  silencedCalls: [],
  callQuality: {},
  clearEnded: () => {},
  requestAttention: () => {},
  setOutboundLine: () => {},
}))

export function selectPhoneDropped(_state: TelephonyTwinState): boolean {
  return false
}

export type { OutboundLine }

/* ----------------------------------------------------------- call controls */
//
// All inert: with no sessions in the store, no vendored surface can reach a
// state where these fire — they exist so the modules type-check and any
// defensive call is a safe no-op.

export async function endCall(_session: TelephonyCall): Promise<boolean> {
  return false
}
export async function declineCall(_session: TelephonyCall): Promise<boolean> {
  return false
}
export async function toggleCallHold(_session: TelephonyCall): Promise<boolean> {
  return false
}
export async function setSpeakerphone(_on: boolean): Promise<boolean> {
  return false
}
export function setCallSilenced(_callId: string, _silenced: boolean): void {}
export async function isMicBlocked(): Promise<boolean> {
  return false
}
export function hasActiveCall(): boolean {
  return false
}
export function isLiveSession(_session: TelephonyCall): boolean {
  return false
}
export async function connectTelephony(): Promise<void> {}
export function disconnectTelephony(): void {}
export function reconnectTelephony(): void {}
export function isLeader(): boolean {
  return false
}

export interface CallFlags {
  muted: boolean
  held: boolean
}

const IDLE_FLAGS: CallFlags = { muted: false, held: false }

export function useCallFlags(_session: TelephonyCall | undefined): CallFlags {
  return IDLE_FLAGS
}

/** No softphone in the console — the health reading is never measurable. */
export function useCallQuality(
  _session: TelephonyCall | undefined,
): LiveCallQuality | undefined {
  return undefined
}

/* --------------------------------------------------------------- telemetry */

export function trackTelephony(_event: string, _data?: Record<string, unknown>): void {}
export function reportTelephonyIssue(_issue: string, _data?: Record<string, unknown>): void {}

/* ------------------------------------------------------------------- lines */

const ONE_DAY_MS = 1000 * 60 * 60 * 24

export const TELEPHONY_NUMBERS_QUERY_OPTIONS = {
  staleTime: ONE_DAY_MS,
  gcTime: ONE_DAY_MS,
  refetchOnWindowFocus: false,
} as const

/** The console's line list is a constant empty stub; nothing to invalidate. */
export function invalidateMyLines(_queryClient: QueryClient): void {}
